import { ApprovalsService } from 'api-clients/monolith';

import messages from './messages';
import { SubjectType } from './types';

/**
 * The operator tokens `Approvals::ValueType::OPERATORS` allows, and the string an
 * admin reads for each.
 *
 * Lives here rather than in the builder because two surfaces render an operator
 * now — the condition row an admin edits, and the Trigger summary on the rule
 * list (AXHE-4464). One map keeps `greater_than` reading the same in both; two
 * copies is how the same condition ends up worded differently in the row that
 * describes it and the panel that edits it.
 *
 * A token with no entry falls back to itself at both call sites. That is a
 * legible degradation for a server-side addition — `soft_matches` reads worse
 * than "soft matches" and is still the operator — where the alternative is an
 * option that renders blank, unpickable and indistinguishable from the field
 * having no operators at all.
 */
export const OPERATOR_MESSAGES: Record<string, keyof typeof messages> = {
  equals: 'operatorEquals',
  not_equals: 'operatorNotEquals',
  contains: 'operatorContains',
  greater_than: 'operatorGreaterThan',
  less_than: 'operatorLessThan',
  is_present: 'operatorIsPresent',
};

/**
 * The compensation comparison tokens `Approvals::Condition::COMP_OPERATORS`
 * allows, and the string an admin reads for each.
 *
 * Separate from `OPERATOR_MESSAGES` because they are a separate vocabulary on the
 * server and two of the three tokens collide with it: a pay condition's
 * `greater_than` compares against the *ceiling* of a band rather than a single
 * value, so "above" is accurate where "greater than" is not — the distinction
 * `triggerSummary` already makes for the same reason.
 */
export const COMPARISON_MESSAGES: Record<string, keyof typeof messages> = {
  greater_than: 'comparisonAbove',
  less_than: 'comparisonBelow',
  outside_of: 'comparisonOutside',
};

/**
 * One entry in the account's typed field catalog: what the field is, which
 * operators its type allows, and which other fields it may be compared against.
 *
 * `comparable_keys` is computed by the server rather than filtered here. The
 * design mock derives it in the browser (`sameTypeKeys = ALL_DATA_KEYS.filter(
 * k => k !== cond.dataKeyName && dataKeyType(k) === dkType)`), but
 * `ValueType.comparable?` is what `ConditionsValidator` enforces on save — so a
 * client that derived its own rule could offer a pairing the server rejects, and
 * the admin would see a 422 on a combination this builder had just presented as
 * legal.
 */
export type CatalogResponse = Awaited<
  ReturnType<typeof ApprovalsService.getInternalApiApprovalsFieldCatalog>
>;

export type CatalogField = NonNullable<CatalogResponse['fields']>[number];

/** One subject type's authorable condition types and standard attributes. */
export type CatalogSubject = NonNullable<CatalogResponse['subjects']>[number];

export type CatalogConditionType = NonNullable<
  CatalogSubject['condition_types']
>[number];

/** A standard attribute, in the same typed shape a data key takes. */
export type CatalogAttribute = NonNullable<CatalogSubject['attributes']>[number];

/**
 * One entry in an entity picker. `label` is null when the record has no name.
 *
 * Widened to the location shape, which carries the `group` it sits under. Every
 * source is read through one `Record<OptionSource, CatalogOption[]>`, and the
 * pickers use `value` and `label` alone — so the optional extra costs nothing and
 * keeps the sources one type rather than branching a map four ways.
 */
export type CatalogOption = NonNullable<
  NonNullable<CatalogResponse['options']>['locations']
>[number];

/**
 * Everything the builder needs the server to have decided for it.
 *
 * `unaryOperators` is shipped rather than restated here for the same reason
 * `comparable_keys` is. A client-side copy of `ValueType::UNARY_OPERATORS`
 * matches today, but adding one server-side would make this builder demand an
 * operand for it — `validateCondition` returning `no_value` on a condition the
 * server accepts. That is the 422 case in reverse, and it would be silent: the
 * list is load-bearing in validation, in serialisation, and in whether a
 * right-hand side is rendered at all.
 *
 * The same argument extends to every field below (AXHE-4383). Which condition
 * types a subject may be gated on, where each may sit, which attributes the
 * evaluation context can actually read, and which types may appear only once are
 * all rules `ConditionsValidator` enforces on save. Restating any of them here
 * would be a second copy free to drift.
 */
export interface FieldCatalog {
  fields: CatalogField[];
  unaryOperators: string[];
  subjects: CatalogSubject[];
  singleUseTypes: string[];
  comparisonOperators: string[];
  attributeScopes: string[];
  sourcedAttributes: { scope: string; attributes: string[] }[];
  options: Record<OptionSource, CatalogOption[]>;
  /** Option lists the server's per-request cap cut short. */
  truncatedOptions: string[];
}

/**
 * The catalog, or the reason it isn't here yet.
 *
 * Kept as a state rather than flattened to an array on the way in, because
 * "still loading", "the request failed" and "this account has no fields to gate
 * on" are three different things to say to an admin and an empty array says the
 * third one for all of them. On a failed fetch that leaves the builder with an
 * empty field picker, no message and no retry — and on an *existing* rule it is
 * worse, because the stored rows still render while their field select has no
 * matching option, so a rule with live conditions reads as though it had none.
 */
export type CatalogState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; catalog: FieldCatalog };

/** Which side of the comparison the right-hand operand comes from. */
export type CompareTo = 'value' | 'dataKey';

/**
 * How a set of conditions combines (AXHE-3930).
 *
 * One operator per set, not a nested expression: the level's conditions are all
 * ANDed or all ORed. That flatness was the deliberate simplification — it cannot
 * express `(A AND B) OR C`, and `Approvals::Rule::CONDITION_LOGICS` is the same
 * two values, so nothing here is narrower than what can be stored.
 */
export type ConditionLogic = 'and' | 'or';

/**
 * Where a condition sits, and the argument `ConditionsValidator` takes.
 *
 * It is not a presentational distinction. A rule's *scope* has no fail-closed
 * protection — an unevaluatable scope condition silently drops the whole rule —
 * so the server refuses some types and some attributes there that it accepts on a
 * level, where the same unknown holds the level for a human instead.
 */
export type ConditionScope = 'scope' | 'level';

/**
 * Why a condition type is offered but cannot be authored anywhere.
 *
 * The one token `Approvals::ConditionCatalog` emits today. Matched rather than
 * assumed: `unavailable_reason` is an open string in the contract, so a type
 * blocked for some later reason must not be explained by a sentence about pay.
 */
export const UNAVAILABLE_COMPENSATION_UNITS = 'compensation_units';

// `Approvals::Condition`'s type tokens. Declared here rather than in each
// consumer because three modules read them now: this builder writes them,
// `ruleForm` splits stored rows on them, and `triggerSummary` words them.
export const ALWAYS = 'always';
export const DATA_KEY_BASED = 'data_key_based';
export const ATTRIBUTE_BASED = 'attribute_based';
export const POSITION_BASED = 'position_based';
export const LOCATION_BASED = 'location_based';
export const OPENING_BASED = 'opening_based';
export const COMPENSATION_BASED = 'compensation_based';

/**
 * The condition types this build can read and write.
 *
 * `always` is absent deliberately. It is one of the two ways "applies to
 * everything" is stored, which is what the rule's own "Apply this rule to" radio
 * chooses (AXHE-4467) — so it is not a row an admin authors, and a level with no
 * conditions already says the same thing.
 *
 * Anything outside this list is a type a later server ships and this build does
 * not understand. Those rows are carried through untouched rather than dropped —
 * see `passthroughConditions`.
 */
export const AUTHORABLE_TYPES = [
  COMPENSATION_BASED,
  DATA_KEY_BASED,
  ATTRIBUTE_BASED,
  POSITION_BASED,
  OPENING_BASED,
  LOCATION_BASED,
] as const;

export type ConditionType = (typeof AUTHORABLE_TYPES)[number];

const isAuthorableType = (type?: string): type is ConditionType =>
  AUTHORABLE_TYPES.includes(type as ConditionType);

/**
 * Which entity picker a condition draws its options from.
 *
 * `location_based` draws on two of them: `locations` by name and
 * `location_groups` by external_id, which is what each side of that condition
 * stores. They are separate sources rather than one nested list because a group
 * is selectable in its own right — an admin may scope a rule to a group holding
 * no locations, and nesting would drop exactly that group.
 */
export type OptionSource =
  | 'positions'
  | 'locations'
  | 'openings'
  | 'location_groups';

/**
 * Which entity a `attribute_based` condition reads its value from
 * (`Approvals::SubjectContext::SCOPES`).
 *
 * Stored lower case, which is the canonical spelling on the server —
 * `SubjectContext.normalize_scope` downcases whatever arrives, and the design's
 * own mock writes it capitalised, so a stored row can be either. `draftConditions`
 * normalises on the way in so one spelling reaches the picker.
 */
export type AttributeScope = string;

export const DEFAULT_ATTRIBUTE_SCOPE = 'opening';

interface DraftBase {
  /** Stable within an editing session so React keys survive a reorder. */
  key: string;
}

export interface DataKeyConditionDraft extends DraftBase {
  type: typeof DATA_KEY_BASED;
  dataKeyName: string;
  operator: string;
  compareTo: CompareTo;
  /** Used when compareTo is 'value'. */
  value: string;
  /** Used when compareTo is 'dataKey'. */
  compareKey: string;
}

export interface AttributeConditionDraft extends DraftBase {
  type: typeof ATTRIBUTE_BASED;
  attrScope: AttributeScope;
  attrName: string;
  operator: string;
  value: string;
}

export interface PositionConditionDraft extends DraftBase {
  type: typeof POSITION_BASED;
  positions: string[];
}

export interface OpeningConditionDraft extends DraftBase {
  type: typeof OPENING_BASED;
  openings: string[];
}

/**
 * Locations and location *groups* are two lists, not one.
 *
 * The design's mock offers a single grouped picker whose group rows select their
 * member locations, which freezes membership at config time. The server keeps the
 * two apart on purpose — `Approvals::Condition#location_groups` — so a group is
 * stored by `external_id` and its membership resolves at evaluation time: adding a
 * location to a group re-scopes every rule naming it, and renaming a group cannot
 * silently un-gate one. Two pickers is what makes that difference visible.
 */
export interface LocationConditionDraft extends DraftBase {
  type: typeof LOCATION_BASED;
  /** Location names — pre-existing shape, matched against the opening's own. */
  locations: string[];
  /** LocationGroup external_ids. */
  locationGroups: string[];
}

export interface CompensationConditionDraft extends DraftBase {
  type: typeof COMPENSATION_BASED;
  comparisonOperator: string;
  /** When true the band comes from the opening's posted range. */
  useJobPayRange: boolean;
  /** Single amount for greater_than / less_than. */
  threshold: string;
  rangeMin: string;
  rangeMax: string;
}

export type ConditionDraft =
  | DataKeyConditionDraft
  | AttributeConditionDraft
  | PositionConditionDraft
  | OpeningConditionDraft
  | LocationConditionDraft
  | CompensationConditionDraft;

/**
 * Whether this operator ignores the right-hand side entirely.
 *
 * Answered from the catalog rather than from a constant here — see
 * `FieldCatalog`. A unary condition needs neither a value nor a compare key, so
 * requiring one would block a legal condition.
 */
export const isUnary = (catalog: FieldCatalog, operator: string) =>
  catalog.unaryOperators.includes(operator);

/**
 * A key for a new row that nothing in the set is already using.
 *
 * Restores the property `DraftBase#key` documents above — stable within an
 * editing session — which minting from `conditions.length` did not give. Length
 * is a count, not a sequence: with two new rows, removing the first leaves
 * `…-new-1` mounted and the next add re-mints `…-new-1`. That is not merely a
 * duplicate React key. Every mutation routes on `key`, so `update` replaces
 * *both* rows and remove deletes *both* — and on a rule's scope the survivor is
 * the one the admin already configured, so filling in the new row overwrites it
 * and an `and` scope of two conditions silently becomes one. A gate that
 * broadens is the wrong direction to fail.
 *
 * Derived from the set rather than from a counter, because the panel's
 * `mintLevelKey` ref cannot be borrowed here: the scope builder unmounts every
 * time the "Apply this rule to" choice flips to "All …" while its drafted rows
 * stay on the draft, so a per-instance counter would reset to zero underneath
 * keys that are still in use. Reading the keys that exist cannot desynchronise
 * from them.
 */
export const mintConditionKey = (
  idPrefix: string,
  conditions: ConditionDraft[],
): string => {
  const used = new Set(conditions.map(condition => condition.key));
  // Starts at the count, so the common case — nothing removed yet — mints the
  // same readable keys as before and walks forward only when it has to.
  let index = conditions.length;
  while (used.has(`${idPrefix}-new-${index}`)) index += 1;
  return `${idPrefix}-new-${index}`;
};

/**
 * A blank row of one type.
 *
 * Each type's defaults are the server's own safe reading rather than the mock's
 * convenience. `useJobPayRange` starts true and `comparisonOperator`
 * `outside_of`, matching the design's `condTypeOptions` handler — but the flag is
 * carried as a real boolean and always serialised, because
 * `Approvals::Condition#use_job_pay_range?` demands a literal `true` and reads an
 * absent key as "manual amounts", which the validator then insists are present.
 */
export const emptyConditionOfType = (
  key: string,
  type: ConditionType,
): ConditionDraft => {
  switch (type) {
    case ATTRIBUTE_BASED:
      return {
        key,
        type: ATTRIBUTE_BASED,
        attrScope: DEFAULT_ATTRIBUTE_SCOPE,
        attrName: '',
        operator: '',
        value: '',
      };
    case POSITION_BASED:
      return { key, type: POSITION_BASED, positions: [] };
    case OPENING_BASED:
      return { key, type: OPENING_BASED, openings: [] };
    case LOCATION_BASED:
      return { key, type: LOCATION_BASED, locations: [], locationGroups: [] };
    case COMPENSATION_BASED:
      return {
        key,
        type: COMPENSATION_BASED,
        comparisonOperator: 'outside_of',
        useJobPayRange: true,
        threshold: '',
        rangeMin: '',
        rangeMax: '',
      };
    default:
      return {
        key,
        type: DATA_KEY_BASED,
        dataKeyName: '',
        operator: '',
        // Comparing to a literal is the common case and the mock's default; the
        // key-to-key mode is opt-in.
        compareTo: 'value',
        value: '',
        compareKey: '',
      };
  }
};

export const fieldFor = (catalog: FieldCatalog, key: string) =>
  catalog.fields.find(field => field.key === key);

/** One subject type's half of the catalog. */
export const subjectCatalogFor = (
  catalog: FieldCatalog,
  subjectType: SubjectType,
): CatalogSubject | undefined =>
  catalog.subjects.find(entry => entry.subject_type === subjectType);

export const attributeFor = (
  catalog: FieldCatalog,
  subjectType: SubjectType,
  key: string,
): CatalogAttribute | undefined =>
  subjectCatalogFor(catalog, subjectType)?.attributes?.find(
    attribute => attribute.key === key,
  );

/**
 * The condition types offered for this subject, in the server's order.
 *
 * Every entry the server sent is returned, including the ones it says cannot be
 * authored — the builder renders those disabled with the reason, because
 * "compensation gating exists and is waiting on a pay period" is the one thing an
 * empty list cannot say (AXHE-4383). `isTypeAuthorable` is what decides whether a
 * given entry may actually be picked here.
 */
export const conditionTypesFor = (
  catalog: FieldCatalog,
  subjectType: SubjectType,
): CatalogConditionType[] => subjectCatalogFor(catalog, subjectType)?.condition_types ?? [];

/** Whether the server allows this type at this scope. */
export const isTypeAuthorable = (
  entry: CatalogConditionType,
  scope: ConditionScope,
) => (entry.scopes ?? []).includes(scope);

/**
 * The attributes the evaluation context can read, for one scope.
 *
 * `ConditionsValidator#unsourced_attribute_error` refuses an attribute outside
 * this list in a rule's *scope*, where an unevaluatable condition silently drops
 * the rule. At a level it is accepted, so the builder offers the whole list and
 * says what an unsourced one will do instead of withholding it.
 */
export const sourcedAttributeNames = (
  catalog: FieldCatalog,
  scope: AttributeScope,
): string[] =>
  catalog.sourcedAttributes.find(entry => entry.scope === scope)?.attributes ?? [];

export const isAttributeSourced = (
  catalog: FieldCatalog,
  attrScope: AttributeScope,
  attrName: string,
) => sourcedAttributeNames(catalog, attrScope).includes(attrName);

/**
 * The attribute names an attribute row may choose from.
 *
 * On a rule's scope that is only what the context can source, because the server
 * refuses the rest — narrowing the picker is what turns a 422 into a choice that
 * was never offered. On a level it is the whole list.
 */
export const attributeChoices = (
  catalog: FieldCatalog,
  subjectType: SubjectType,
  scope: ConditionScope,
  attrScope: AttributeScope,
): CatalogAttribute[] => {
  const all = subjectCatalogFor(catalog, subjectType)?.attributes ?? [];
  if (scope !== 'scope') return all;

  const sourced = sourcedAttributeNames(catalog, attrScope);
  return all.filter(attribute => sourced.includes(attribute.key ?? ''));
};

/**
 * The attribute scopes an attribute row may choose from.
 *
 * Narrowed on a rule's scope to the ones that have something to offer, so
 * `attributeChoices` can never hand the picker an empty list — a select with no
 * options and no explanation reads as "this account has no attributes", which is
 * not what an unsourced scope means.
 */
export const attributeScopeChoices = (
  catalog: FieldCatalog,
  subjectType: SubjectType,
  scope: ConditionScope,
): AttributeScope[] =>
  scope === 'scope'
    ? catalog.attributeScopes.filter(
        attrScope => attributeChoices(catalog, subjectType, scope, attrScope).length > 0,
      )
    : catalog.attributeScopes;

/**
 * Types already used by a *different* row, that may only appear once.
 *
 * `Approvals::Condition::SINGLE_USE_TYPES` is rejected in duplicate by
 * `ConditionsValidator#single_use_errors`, which fails the whole rule — so the
 * second one is withheld here rather than offered and then refused. Two pay
 * conditions in one set is a builder mistake, not a meaningful AND.
 */
export const singleUseTypesInUse = (
  catalog: FieldCatalog,
  conditions: ConditionDraft[],
  exceptKey?: string,
): string[] =>
  conditions
    .filter(condition => condition.key !== exceptKey)
    .map(condition => condition.type)
    .filter(type => catalog.singleUseTypes.includes(type));

/**
 * The type a newly added row starts on.
 *
 * The first type the server offers for this subject that is authorable at this
 * scope and not already spent by a single-use row. The design opens a new offer
 * row on pay and a new opening row on location; following the server's order
 * reproduces that while degrading correctly — with pay unavailable the row lands
 * on the next type rather than on a control the admin cannot use.
 */
export const defaultConditionType = (
  catalog: FieldCatalog,
  subjectType: SubjectType,
  scope: ConditionScope,
  conditions: ConditionDraft[],
): ConditionType | undefined => {
  const spent = singleUseTypesInUse(catalog, conditions);

  const entry = conditionTypesFor(catalog, subjectType).find(
    candidate =>
      isAuthorableType(candidate.type) &&
      isTypeAuthorable(candidate, scope) &&
      !spent.includes(candidate.type as string),
  );
  return entry?.type as ConditionType | undefined;
};

/**
 * Change a row's condition type.
 *
 * Everything else is discarded rather than merged: no two types share an operand,
 * so a carried-over field is a value the new type cannot use and the server does
 * not read. Keeping the key is what makes this an edit to the row rather than a
 * remove and an add.
 */
export const withConditionType = (
  condition: ConditionDraft,
  type: ConditionType,
): ConditionDraft => emptyConditionOfType(condition.key, type);

/**
 * Change the condition's data key.
 *
 * Resets the operator, the value and the compare key, because all three are
 * only meaningful relative to a type: `contains` is legal on a string and not on
 * a number, "2026-01-01" is a date value and not a boolean, and a compare key of
 * the old type is no longer comparable. Carrying any of them over produces a
 * condition that looks filled in and fails validation on save — the mock resets
 * them for the same reason.
 *
 * The one exception is a value typed before any field was chosen. The Value box
 * is live then, so discarding its text on the first pick reads as a lost edit;
 * it stays when the new field takes it as typed — see `carriesTypedValue`.
 *
 * `compareTo` is kept when the new field can still be compared to another field,
 * because the admin chose that mode deliberately and re-picking it on every
 * field change would be tedious. It falls back to a literal when the new field
 * has no comparable siblings: left on `dataKey`, the row would open on a
 * compare-key picker with nothing to offer, and the save would be refused as
 * `no_compare_key` until the admin unticked the box.
 *
 * The catalog is required rather than defaulted: with an unloaded catalog every
 * field looks as though it had no comparable siblings, and this would silently
 * drop a mode the admin deliberately chose. The builder does not render a field
 * picker until the catalog is `ready`, so there is no caller without one.
 */
export const withDataKey = (
  condition: DataKeyConditionDraft,
  dataKeyName: string,
  catalog: FieldCatalog,
): DataKeyConditionDraft => {
  const field = fieldFor(catalog, dataKeyName);
  const comparable = field?.comparable_keys ?? [];
  const keepsValue = !condition.dataKeyName && carriesTypedValue(field, condition.value);

  return {
    ...condition,
    dataKeyName,
    operator: '',
    value: keepsValue ? condition.value : '',
    compareKey: '',
    compareTo: comparable.length > 0 ? condition.compareTo : 'value',
  };
};

/**
 * Switch between comparing to a literal and comparing to another field.
 *
 * Clears whichever operand is now unused, so a mode the admin moved away from
 * cannot leave a stale value behind in the saved document.
 */
export const withCompareTo = (
  condition: DataKeyConditionDraft,
  compareTo: CompareTo,
): DataKeyConditionDraft => ({
  ...condition,
  compareTo,
  value: compareTo === 'value' ? condition.value : '',
  compareKey: compareTo === 'dataKey' ? condition.compareKey : '',
});

/**
 * Change an attribute row's attribute, or the entity it is read from.
 *
 * Both reset the operator and the value, for the reason `withDataKey` gives: the
 * operator list comes from the attribute's type and the value from its option
 * list, so neither survives a change of attribute. Changing the *scope* clears
 * the attribute too — on a rule's scope the choices differ per scope, so a name
 * that was valid under one may not be offered under the next.
 */
export const withAttributeName = (
  condition: AttributeConditionDraft,
  attrName: string,
): AttributeConditionDraft => ({ ...condition, attrName, operator: '', value: '' });

export const withAttributeScope = (
  condition: AttributeConditionDraft,
  attrScope: AttributeScope,
): AttributeConditionDraft => ({
  ...condition,
  attrScope,
  attrName: '',
  operator: '',
  value: '',
});

/**
 * Switch a pay condition between the opening's posted band and manual amounts.
 *
 * The amounts are kept when switching to the band rather than cleared, because
 * `toStoredConditions` does not emit them in that state and an admin who ticks
 * the box to compare it does not lose what they typed.
 */
export const withJobPayRange = (
  condition: CompensationConditionDraft,
  useJobPayRange: boolean,
): CompensationConditionDraft => ({ ...condition, useJobPayRange });

export type ConditionError =
  | 'no_data_key'
  | 'unknown_data_key'
  | 'no_operator'
  | 'unsupported_operator'
  | 'no_value'
  | 'no_compare_key'
  | 'unknown_compare_key'
  | 'unknown_value'
  | 'bad_value'
  | 'no_attribute'
  | 'unknown_attribute'
  | 'unsourced_attribute'
  | 'no_positions'
  | 'no_locations'
  | 'no_openings'
  | 'type_unavailable'
  | 'type_not_allowed'
  | 'no_comparison_operator'
  | 'no_amount'
  | 'no_range'
  | 'bad_range'
  | 'duplicate_single_use';

/** What a row is being judged against: `ConditionsValidator`'s two arguments. */
export interface ConditionContext {
  subjectType: SubjectType;
  scope: ConditionScope;
}

/** A positive number, the only amount `ConditionsValidator` accepts. */
const positiveAmount = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  // Matches `ValueType.coerce_number`, which strips currency and thousands
  // separators before parsing — so an amount this accepts is one the server
  // accepts, and "$50,000" is not reported as non-numeric on a field the server
  // would have taken.
  const cleaned = trimmed.replace(/[$,\s]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return undefined;
  const value = Number(cleaned);
  return value > 0 ? value : undefined;
};

/**
 * Whether the field would accept this literal, mirroring
 * `Approvals::Field#accepts_value?`.
 *
 * The widget constrains what an admin can *enter* — a number input, a date
 * picker, a select over `allowed_values` — so a mismatch here is always a
 * **stored** value the field has moved out from under: its `approval_value_type`
 * changed, or the account edited the option list. `ConditionsValidator` refuses
 * that row on save, and `SaveRule` recreates every level on any save, so the
 * refusal lands on a rule the admin may only have renamed. Naming the row is the
 * only way they can tell which one to fix.
 *
 * `ValueType.coerce` is the shape being mirrored, not `positiveAmount`: a data key
 * may legitimately hold a negative number or a zero, unlike a pay figure.
 */
const acceptsValue = (
  field: { type?: string | null; allowed_values?: string[] } | undefined,
  value: string,
): ConditionError | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return 'no_value';

  // An enumeration is closed: `accepts_value?` answers on list membership alone
  // and never falls through to a type coercion.
  if (field?.allowed_values?.length) {
    return field.allowed_values.includes(trimmed) ? undefined : 'unknown_value';
  }

  switch (field?.type) {
    // Matches `ValueType.coerce_number`, which strips currency and thousands
    // separators before parsing.
    case 'number':
      return /^-?\d+(\.\d+)?$/.test(trimmed.replace(/[$,\s]/g, ''))
        ? undefined
        : 'bad_value';
    // `Date.parse` is looser than Ruby's `Date.parse`, so this can pass a value
    // the server then rejects. Deliberate: the reverse — refusing a date the
    // server would take — would block a legal condition, and the server is the
    // authority either way.
    case 'date':
      return Number.isNaN(Date.parse(trimmed)) ? 'bad_value' : undefined;
    // `ValueType.coerce_boolean`'s own vocabulary.
    case 'boolean':
      return ['true', 'false', 'yes', 'no', '1', '0', 't', 'f'].includes(
        trimmed.toLowerCase(),
      )
        ? undefined
        : 'bad_value';
    default:
      return undefined;
  }
};

/**
 * Whether text typed into the Value box before a field existed can stay once one
 * is chosen.
 *
 * Only when the new field accepts it and its control can show it as typed. A
 * number input shows a plain decimal, not "$50,000" or " 50000 ", and it blanks
 * either while the draft would keep it. A date field always starts
 * empty: its picker shows only ISO dates, and `acceptsValue`'s date check is
 * looser than the server's, so a kept value could sit unseen and then be refused
 * on save.
 *
 * A function declaration, so `withDataKey` above can call it.
 */
function carriesTypedValue(field: CatalogField | undefined, value: string) {
  if (!field || acceptsValue(field, value) || field.type === 'date') return false;
  return field.type !== 'number' || /^-?\d+(\.\d+)?$/.test(value);
}

/**
 * Whether this amount is one `ConditionsValidator` would take.
 *
 * Exported because `no_range` is raised for the pair — either bound failing
 * produces the one token — so only the render can decide which box to mark. Left
 * to a single flag, the red border and the helper text landed on Minimum even when
 * Maximum was the empty one, pointing the admin at the field that was already
 * right.
 */
export const isUsableAmount = (raw: string) => positiveAmount(raw) !== undefined;

const validateDataKey = (
  condition: DataKeyConditionDraft,
  catalog: FieldCatalog,
): ConditionError | undefined => {
  if (!condition.dataKeyName) return 'no_data_key';

  const field = fieldFor(catalog, condition.dataKeyName);
  if (!field) return 'unknown_data_key';

  if (!condition.operator) return 'no_operator';
  if (!(field.operators ?? []).includes(condition.operator)) {
    return 'unsupported_operator';
  }

  // A unary operator carries no right-hand side at all, so neither operand is
  // required and demanding one would refuse a condition the server accepts.
  if (isUnary(catalog, condition.operator)) return undefined;
  if (condition.compareTo === 'dataKey') {
    if (!condition.compareKey) return 'no_compare_key';

    // Membership, not just presence. `comparable_keys` lists the fields the
    // server will accept as the right-hand side, so a key absent from it is one
    // of the two stale states `ConditionsValidator#errors_for_value` rejects:
    // the target field is gone ("unknown data key"), or both still exist and a
    // type changed under them ("cannot be compared to"). Either way the save
    // 422s and the panel could only report the generic "we couldn't save this
    // rule" — naming no row and no control. Checked here so the row says which
    // one, the same way `unsupported_operator` already does for the operator.
    if (!(field.comparable_keys ?? []).includes(condition.compareKey)) {
      return 'unknown_compare_key';
    }

    return undefined;
  }
  return acceptsValue(field, condition.value);
};

const validateAttribute = (
  condition: AttributeConditionDraft,
  catalog: FieldCatalog,
  context: ConditionContext,
): ConditionError | undefined => {
  if (!condition.attrName) return 'no_attribute';

  const attribute = attributeFor(catalog, context.subjectType, condition.attrName);
  if (!attribute) return 'unknown_attribute';

  // Only in a rule's scope, and only because that is where the server refuses
  // it. On a level the same attribute holds every request for a human until it
  // is sourced, which over-gates rather than slipping a gate — the panel says so
  // instead of blocking the save.
  if (
    context.scope === 'scope' &&
    !isAttributeSourced(catalog, condition.attrScope, condition.attrName)
  ) {
    return 'unsourced_attribute';
  }

  if (!condition.operator) return 'no_operator';
  if (!(attribute.operators ?? []).includes(condition.operator)) {
    return 'unsupported_operator';
  }

  if (isUnary(catalog, condition.operator)) return undefined;
  return acceptsValue(attribute, condition.value);
};

/**
 * A pay condition's operands. Whether the type may exist here at all is
 * `unavailableTypeError`'s question, asked before this runs.
 */
const validateCompensation = (
  condition: CompensationConditionDraft,
  catalog: FieldCatalog,
): ConditionError | undefined => {
  if (!catalog.comparisonOperators.includes(condition.comparisonOperator)) {
    return 'no_comparison_operator';
  }

  // The opening supplies the band, so there is nothing here to check.
  if (condition.useJobPayRange) return undefined;

  if (condition.comparisonOperator === 'outside_of') {
    const min = positiveAmount(condition.rangeMin);
    const max = positiveAmount(condition.rangeMax);
    if (min === undefined || max === undefined) return 'no_range';
    return min < max ? undefined : 'bad_range';
  }

  return positiveAmount(condition.threshold) === undefined ? 'no_amount' : undefined;
};

/**
 * Whether this row's type may exist here at all, asked before its operands.
 *
 * `typeSelect` carries a row's own type into the picker as a synthetic entry when
 * the catalog does not list it, so a stored condition of a type the subject never
 * allows renders fully populated rather than blank. Nothing downstream catches
 * it: `catalog.fields` is top level rather than per subject, so `validateDataKey`
 * resolves a data key on an opening rule as happily as on an offer.
 * `ConditionsValidator#wrong_subject_for_data_key_error` then refuses the save,
 * and a rule-level error names neither the row nor the control.
 *
 * Two answers, because the admin's move differs. A type the server offers *with*
 * an `unavailable_reason` is blocked for now — pay, until AXHE-4052 — so the copy
 * says wait or remove. A type absent from the subject's list, or offered and not
 * placeable at this scope, will never work here, so the copy says remove.
 *
 * `attribute_based` is left to its own branch. With nothing sourced the server
 * withholds it from a rule's scope and gives no reason, and `unsourced_attribute`
 * names the way out — gate a level on it — that this generic error cannot.
 */
const unavailableTypeError = (
  condition: ConditionDraft,
  catalog: FieldCatalog,
  context: ConditionContext,
): ConditionError | undefined => {
  const entry = conditionTypesFor(catalog, context.subjectType).find(
    candidate => candidate.type === condition.type,
  );
  if (!entry) return 'type_not_allowed';
  if (isTypeAuthorable(entry, context.scope)) return undefined;
  if (condition.type === ATTRIBUTE_BASED) return undefined;
  return entry.unavailable_reason ? 'type_unavailable' : 'type_not_allowed';
};

/**
 * Validated against the catalog, not just against emptiness.
 *
 * A stored condition whose field or operator is no longer offered — the key was
 * removed from the account, or the field's `approval_value_type` changed under
 * it — renders as a blank select and would pass a non-empty check, then 422 on
 * save. Naming it at load is the only point at which the admin can act on it.
 *
 * Every branch mirrors a refusal in `Approvals::ConditionsValidator`, and the
 * two arguments that decide those refusals — the subject type and whether this is
 * a rule's scope or a level — are passed rather than assumed.
 */
export const validateCondition = (
  condition: ConditionDraft,
  catalog: FieldCatalog,
  context: ConditionContext,
): ConditionError | undefined => {
  const unavailable = unavailableTypeError(condition, catalog, context);
  if (unavailable) return unavailable;

  switch (condition.type) {
    case DATA_KEY_BASED:
      return validateDataKey(condition, catalog);
    case ATTRIBUTE_BASED:
      return validateAttribute(condition, catalog, context);
    case POSITION_BASED:
      return condition.positions.length > 0 ? undefined : 'no_positions';
    case OPENING_BASED:
      return condition.openings.length > 0 ? undefined : 'no_openings';
    // Either list is a usable scope; both empty is not. The server takes the same
    // view — `ConditionsValidator#errors_for_location`.
    case LOCATION_BASED:
      return condition.locations.length > 0 || condition.locationGroups.length > 0
        ? undefined
        : 'no_locations';
    case COMPENSATION_BASED:
      return validateCompensation(condition, catalog);
    default:
      return undefined;
  }
};

/**
 * Errors that belong to the set rather than to one row.
 *
 * Reported per offending row, so the message lands on a control the admin can
 * act on: a duplicate single-use type is fixed by changing or removing *one* of
 * the two rows, and the row this flags is the later one — the first stays as the
 * admin wrote it.
 */
export const duplicateSingleUseKeys = (
  catalog: FieldCatalog,
  conditions: ConditionDraft[],
): Set<string> => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  conditions.forEach(condition => {
    if (!catalog.singleUseTypes.includes(condition.type)) return;
    if (seen.has(condition.type)) {
      duplicates.add(condition.key);
      return;
    }
    seen.add(condition.type);
  });

  return duplicates;
};

/** Whether any row in the set is invalid, for the whole-set check `validate` runs. */
export const conditionSetErrors = (
  conditions: ConditionDraft[],
  catalog: FieldCatalog,
  context: ConditionContext,
): boolean => {
  if (duplicateSingleUseKeys(catalog, conditions).size > 0) return true;
  return conditions.some(condition => validateCondition(condition, catalog, context));
};

/** The stored shape, as `Approvals::Condition` reads it. */
export interface StoredCondition {
  type: string;
  dataKeyName?: string;
  dataKeyOperator?: string;
  dataKeyCompareTo?: CompareTo;
  dataKeyValue?: string;
  dataKeyCompareKey?: string;
  attrScope?: string;
  attrName?: string;
  attrOperator?: string;
  attrValue?: string;
  positions?: unknown[];
  locations?: unknown[];
  locationGroups?: unknown[];
  openings?: unknown[];
  comparisonOperator?: string;
  useJobPayRange?: boolean;
  threshold?: unknown;
  rangeMin?: unknown;
  rangeMax?: unknown;
  [key: string]: unknown;
}

/**
 * A stored list of identifiers, as the picker needs it.
 *
 * Trimmed and de-blanked, matching `.presence` on the server side: the jsonb
 * accepts `["", "  "]`, and a row of blanks is a "scope" naming nothing —
 * `ConditionsValidator` refuses it, so drafting it as a selected value would show
 * the admin a chip they never chose.
 */
const asList = (raw: unknown): string[] =>
  (Array.isArray(raw) ? raw : [])
    .map(entry => String(entry ?? '').trim())
    .filter(entry => entry.length > 0);

/**
 * The same filter on the way out.
 *
 * `asList` defends the read path against a jsonb array of blanks, and the write
 * path needs the same guard for a different reason: a picker's options come from
 * the catalog, where `ApprovalConditionOption#value` is optional in the generated
 * client. The server never emits an option without one today — `ConditionCatalog`
 * filters `external_id: nil` and always supplies a name — but a `MenuItem` whose
 * value is `undefined` would put `undefined` straight into the stored array, and
 * `ConditionsValidator` reads that as a scope naming nothing.
 */
const asStoredList = (values: string[]): string[] =>
  values.map(entry => String(entry ?? '').trim()).filter(entry => entry.length > 0);

const asAmount = (raw: unknown): string =>
  raw === undefined || raw === null ? '' : String(raw);

/**
 * Read one stored row into a draft, or `undefined` for a type this build does not
 * author.
 *
 * An unrecognised type is not an error and not a blank row: it is a condition a
 * later server wrote, and `passthroughConditions` carries it back untouched.
 */
const draftCondition = (
  row: StoredCondition,
  key: string,
): ConditionDraft | undefined => {
  switch (row.type) {
    case DATA_KEY_BASED:
      return {
        key,
        type: DATA_KEY_BASED,
        dataKeyName: row.dataKeyName ?? '',
        operator: row.dataKeyOperator ?? '',
        // Narrowed, not cast. Both render branches test the value explicitly, so
        // a third spelling — legacy data, or a hand-written payload — rendered a
        // row with a field and an operator and no right-hand side at all, while
        // `validateDataKey` still judged the value it was not showing. Anything
        // that is not the opt-in mode is the literal mode.
        compareTo: row.dataKeyCompareTo === 'dataKey' ? 'dataKey' : 'value',
        value: row.dataKeyValue ?? '',
        compareKey: row.dataKeyCompareKey ?? '',
      };
    case ATTRIBUTE_BASED:
      return {
        key,
        type: ATTRIBUTE_BASED,
        // Lower-cased on the way in: `SubjectContext.normalize_scope` downcases,
        // and the design's mock stores "Opening", so both spellings exist in
        // saved rules. One spelling has to reach the picker or a stored row would
        // render with no scope selected.
        attrScope: (row.attrScope ?? DEFAULT_ATTRIBUTE_SCOPE).toString().toLowerCase(),
        attrName: row.attrName ?? '',
        operator: row.attrOperator ?? '',
        value: row.attrValue ?? '',
      };
    case POSITION_BASED:
      return { key, type: POSITION_BASED, positions: asList(row.positions) };
    case OPENING_BASED:
      return { key, type: OPENING_BASED, openings: asList(row.openings) };
    case LOCATION_BASED:
      return {
        key,
        type: LOCATION_BASED,
        locations: asList(row.locations),
        locationGroups: asList(row.locationGroups),
      };
    case COMPENSATION_BASED:
      return {
        key,
        type: COMPENSATION_BASED,
        comparisonOperator: row.comparisonOperator ?? '',
        // `=== true`, not truthiness: `Approvals::Condition#use_job_pay_range?`
        // requires a literal true, so anything else is the manual-amounts case
        // and drafting it as checked would hide the amounts the rule is really
        // using.
        useJobPayRange: row.useJobPayRange === true,
        threshold: asAmount(row.threshold),
        rangeMin: asAmount(row.rangeMin),
        rangeMax: asAmount(row.rangeMax),
      };
    default:
      return undefined;
  }
};

/**
 * Read stored conditions into drafts.
 *
 * Keys are minted from the row's index in the *stored* array rather than in the
 * drafted one, so a set holding an unauthorable row still gives every draft a
 * distinct key.
 */
export const draftConditions = (stored: StoredCondition[] = []): ConditionDraft[] =>
  stored
    .map((row, index) => draftCondition(row, `condition-${index}`))
    .filter((draft): draft is ConditionDraft => draft !== undefined);

/**
 * Rows this build cannot author, carried back untouched.
 *
 * Normally empty now that all six of the design's condition types are
 * first-class (AXHE-4383). Kept because the alternative is deleting a condition
 * written by a server newer than this bundle: `toPayload` replaces the whole
 * condition set, so a row this build dropped would be dropped from the rule.
 *
 * A level's `always` row belongs here — it is a real condition there, meaning
 * "this level applies whatever else is true" — and a rule's does not; see
 * `scopePassthroughConditions`.
 */
export const passthroughConditions = (stored: StoredCondition[] = []) =>
  stored.filter(row => !isAuthorableType(row.type));

/**
 * The same split for a rule's *scope*, with `always` rows held back.
 *
 * A level's `always` row is a real condition — the level applies whatever else
 * is true — so `passthroughConditions` carries it. A rule's is not: it is how
 * "applies to everything" is stored, which is the very thing the scope radio
 * chooses. Carried through as passthrough it would survive into the conditional
 * branch, where under `or` logic it makes the whole scope match everything —
 * a rule that reads "Offers that meet conditions" and gates every offer.
 */
export const scopePassthroughConditions = (stored: StoredCondition[] = []) =>
  stored.filter(row => !isAuthorableType(row.type) && row.type !== ALWAYS);

/** Whether this scope row narrows what the rule applies to. */
export const isScopingCondition = (row: StoredCondition) => row.type !== ALWAYS;

/**
 * One draft back to the stored shape.
 *
 * Each type emits exactly the keys `Approvals::Condition` reads for it and no
 * others. The unused operand is omitted rather than sent empty, because the
 * server reads the presence of a key to decide which side it looks at and an
 * empty string is indistinguishable from a value the admin cleared:
 * `dataKeyCompareTo` selects between `dataKeyValue` and `dataKeyCompareKey`, and
 * `useJobPayRange` selects between the opening's band and the manual amounts.
 *
 * Literals are stored trimmed, matching what `validateCondition` accepts. Storing
 * `"  Engineering  "` when the check that let it through looked at `"Engineering"`
 * makes an equality comparison against a data key value a silent non-match.
 */
const storeCondition = (
  draft: ConditionDraft,
  catalog: FieldCatalog,
): StoredCondition => {
  switch (draft.type) {
    case ATTRIBUTE_BASED: {
      const row: StoredCondition = {
        type: ATTRIBUTE_BASED,
        attrScope: draft.attrScope,
        attrName: draft.attrName,
        attrOperator: draft.operator,
      };
      if (!isUnary(catalog, draft.operator)) row.attrValue = draft.value.trim();
      return row;
    }
    case POSITION_BASED:
      return { type: POSITION_BASED, positions: asStoredList(draft.positions) };
    case OPENING_BASED:
      return { type: OPENING_BASED, openings: asStoredList(draft.openings) };
    case LOCATION_BASED:
      return {
        type: LOCATION_BASED,
        locations: asStoredList(draft.locations),
        locationGroups: asStoredList(draft.locationGroups),
      };
    case COMPENSATION_BASED: {
      const row: StoredCondition = {
        type: COMPENSATION_BASED,
        comparisonOperator: draft.comparisonOperator,
        // Always sent, and as a real boolean. An absent key reads as false on the
        // server — the safe default — so omitting it when checked would silently
        // switch the condition to manual amounts it does not have.
        useJobPayRange: draft.useJobPayRange,
      };
      if (draft.useJobPayRange) return row;

      if (draft.comparisonOperator === 'outside_of') {
        row.rangeMin = draft.rangeMin.trim();
        row.rangeMax = draft.rangeMax.trim();
      } else {
        row.threshold = draft.threshold.trim();
      }
      return row;
    }
    default: {
      const row: StoredCondition = {
        type: DATA_KEY_BASED,
        dataKeyName: draft.dataKeyName,
        dataKeyOperator: draft.operator,
      };

      if (isUnary(catalog, draft.operator)) return row;

      row.dataKeyCompareTo = draft.compareTo;
      if (draft.compareTo === 'dataKey') {
        row.dataKeyCompareKey = draft.compareKey;
      } else {
        row.dataKeyValue = draft.value.trim();
      }
      return row;
    }
  }
};

/** Drafts back to the stored shape, with the untouched rows preserved. */
export const toStoredConditions = (
  drafts: ConditionDraft[],
  passthrough: StoredCondition[],
  catalog: FieldCatalog,
): StoredCondition[] => [
  ...passthrough,
  ...drafts.map(draft => storeCondition(draft, catalog)),
];
